import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../../ApiConfig';
import style from '../../../css/admin/Uploadform.module.css';
import { showCustomAlerterror } from '../../customalert';
import { useNavigate } from 'react-router-dom';
import { getSecureToken } from '../../ApisService/apiService';

const UploadStudyMaterialPage = () => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [videoFiles, setVideoFiles] = useState([]);
    const [docFiles, setDocFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [speed, setSpeed] = useState(0);
    const [online, setOnline] = useState(navigator.onLine);
    const [message, setMessage] = useState('');

    const navigate = useNavigate();

    useEffect(() => {
        const isAdmin = localStorage.getItem('islogin');
        if (!isAdmin || isAdmin !== 'true') {
            navigate('/admin');
        }
    }, []);

    useEffect(() => {
        const checkSession = async () => {
            const { secrettoken, ts } = await getSecureToken();
            try {
                const res = await axios.get(`${API_BASE_URL}adminauth/check_admin_session.php`, {
                    headers: {
                        'X-TOKEN': secrettoken,
                        'X-TIMESTAMP': ts,
                    },
                    withCredentials: true,
                });

                console.log(res.data);

                if (res.data.status !== "ok") {
                    navigate('/admin');
                }

            } catch (error) {
                console.error("Session check failed", error.response?.data || error.message);
                navigate('/admin');
            }
        };

        checkSession();
    }, []);


    const MAX_TOTAL_FILES = 10;
    const MAX_FILE_SIZE_MB = 4096; // 4 GB limit

    useEffect(() => {
        const handleOnline = () => setOnline(true);
        const handleOffline = () => setOnline(false);

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    const resetFileInputs = () => {
        document.querySelectorAll("input[type='file']").forEach(input => {
            input.value = '';
        });
    };

    const handleFileChange = (event, type) => {
        const selectedFiles = Array.from(event.target.files);
        const totalFiles = videoFiles.length + docFiles.length;

        if (totalFiles + selectedFiles.length > MAX_TOTAL_FILES) {
            showCustomAlerterror(`You can upload a maximum of ${MAX_TOTAL_FILES} files in total.`);
            resetFileInputs();
            return;
        }

        const validFiles = selectedFiles.filter(file => {
            if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
                showCustomAlerterror(`${file.name} exceeds the ${MAX_FILE_SIZE_MB}MB limit.`);
                return false;
            }
            return true;
        });

        if (type === 'video') {
            setVideoFiles(prev => [...prev, ...validFiles]);
        } else {
            setDocFiles(prev => [...prev, ...validFiles]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUploading(true);
        setMessage('');
        setProgress(0);
        setSpeed(0);

        const totalFiles = videoFiles.length + docFiles.length;

        if (totalFiles > MAX_TOTAL_FILES) {
            showCustomAlerterror(`You can upload a maximum of ${MAX_TOTAL_FILES} files in total.`);
            setUploading(false);
            return;
        }

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        videoFiles.forEach(file => formData.append('videos[]', file));
        docFiles.forEach(file => formData.append('documents[]', file));

        const startTime = Date.now();

        try {
            const { secrettoken, ts } = await getSecureToken();

            await axios.post(`${API_BASE_URL}studymaterial/studymaterial_upload.php`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                    'X-TOKEN': secrettoken,
                    'X-TIMESTAMP': ts,
                },
                withCredentials: true,
                onUploadProgress: (event) => {
                    const percent = Math.round((event.loaded * 100) / event.total);
                    const elapsed = (Date.now() - startTime) / 1000;
                    const kbps = (event.loaded / 1024 / elapsed).toFixed(2);
                    setProgress(percent);
                    setSpeed(kbps);
                }
            });

            setMessage('Upload successful!');
            setTitle('');
            setDescription('');
            setVideoFiles([]);
            setDocFiles([]);
            resetFileInputs();

        } catch (err) {
            setMessage('Upload failed. Please try again.');
            showCustomAlerterror('Upload failed. Please try again.');
        } finally {
            setUploading(false);
        }
    };


    return (
        <div className={style.pageWrapper}>
            <h2 className={style.header}>Upload Study Material</h2>
            <div className={style.formContainer}>
                <form onSubmit={handleSubmit} className={style.uploadForm}>
                    <label>Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Enter title"
                        required
                    />

                    <label>Description</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Enter description"
                    ></textarea>

                    <label>Upload Videos (Max: 5)</label>
                    <input
                        type="file"
                        accept="video/*"
                        multiple
                        onChange={(e) => handleFileChange(e, 'video')}
                    />

                    <label>Upload Documents/Images (Max: 5)</label>
                    <input
                        type="file"
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                        multiple
                        onChange={(e) => handleFileChange(e, 'doc')}
                    />

                    <button type="submit" className={style.uploadButton} disabled={uploading}>
                        {uploading ? 'Uploading...' : 'Save'}
                    </button>
                </form>

                {uploading && (
                    <div className={style.progressWrapper}>
                        <p>Uploading: {progress}%</p>
                        <div className={style.progressBar}>
                            <div className={style.progress} style={{ width: `${progress}%` }}></div>
                        </div>
                        <p>Speed: {speed} KB/s</p>
                    </div>
                )}

                <p className={style.message}>
                    Internet: <span style={{ color: online ? 'green' : 'red' }}>{online ? 'Online' : 'Offline'}</span>
                </p>

                {message && <p className={style.message}>{message}</p>}
            </div>

            <a className={style.goBack} href="/admin_dash/studymaterial_upload">← Go Back</a>
        </div>
    );
};

export default UploadStudyMaterialPage;
